import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { GridCellProps, Index, MultiGrid } from 'react-virtualized';
import isNil from 'lodash/isNil';
import { MultiGridTable } from '../../../MultiGridTable';
import ErrorChartInfo from '../../ErrorChartInfo';
import { ChartViewType } from '../../types';

interface IProps {
  cellRenderer: (props: GridCellProps) => React.ReactNode;
  headersCount: number;
  columnCount: number;
  rowCount: number;
  columnWidths: number[];
  getColumnWidth: ({ index }: { index: number }) => number;
  multiGridWidth?: number;
  onChangeColumnWidths: (newColWidths: number[]) => void;
  setMultiGridWidth: (multiGridWidth: number) => void;
  presetTableHeight?: number;
}

const DEFAULT_CELL_WIDTH = 160;
const DEFAULT_ROW_HEIGHT = 32;
const DEFAULT_ROW_HEADER_HEIGHT = 48;
const TABLE_HEIGHT = 600;

const getRowHeight = ({ index }: Index) =>
  index === 0 ? DEFAULT_ROW_HEADER_HEIGHT : DEFAULT_ROW_HEIGHT;

const CohortSampleTable = ({
  presetTableHeight = TABLE_HEIGHT,
  headersCount,
  rowCount,
  columnCount,
  columnWidths,
  getColumnWidth,
  onChangeColumnWidths,
  multiGridWidth,
  setMultiGridWidth,
  cellRenderer,
}: IProps) => {
  const ref = useRef<MultiGrid>(null);

  const tableHeight = useMemo(() => {
    // 1 + rows.length, 1 is header
    const height =
      DEFAULT_ROW_HEADER_HEIGHT + rowCount * DEFAULT_ROW_HEIGHT + 10;
    if (height > presetTableHeight) {
      return presetTableHeight;
    }
    return height;
  }, [rowCount, presetTableHeight]);

  const onResize = useCallback(() => {
    if (!ref?.current) {
      return;
    }

    // manually minus 1 to remove unwanted x-axis scroll bar
    const width = ref.current.props.width - 1;

    const allColumnsWidth = columnWidths?.length
      ? columnWidths.reduce((a, b) => a + b, 0)
      : 0;

    // if the sum of all the column widths is less than the width of the multigrid
    // recompute the column widths
    if (width > 0 && (isNil(columnWidths) || allColumnsWidth < width)) {
      const newColWidths = Array.from({ length: headersCount }).map(
        () => Math.max(DEFAULT_CELL_WIDTH, width / headersCount) - 1,
      );
      onChangeColumnWidths(newColWidths);
    }
  }, [columnWidths, headersCount, onChangeColumnWidths]);

  // if the columnWidths array has changed, recompute the multigrid
  useEffect(() => {
    if (ref?.current) {
      ref.current.recomputeGridSize();

      // set the multigrid width state in the useTableColumnWidthState hook
      if (ref.current.props.width !== multiGridWidth) {
        setMultiGridWidth(ref.current.props.width);
      }
    }
  }, [columnWidths, multiGridWidth, setMultiGridWidth]);

  if (!rowCount) {
    return <ErrorChartInfo type={ChartViewType.NoData} />;
  }

  return (
    <div style={{ height: tableHeight }}>
      <MultiGridTable
        ref={ref}
        columnCount={columnCount}
        rowCount={rowCount}
        rowHeight={getRowHeight}
        cellRenderer={cellRenderer}
        columnWidthRenderer={getColumnWidth}
        onResize={onResize}
      />
    </div>
  );
};

export default CohortSampleTable;
